/**
 * Менеджер персистентности CSElement
 * Управляет адаптерами хранилища и предоставляет единый API
 */

import { EventEmitter } from 'eventemitter3';
import { CSElement } from '../core/CSElement';
import {
  PersistenceManager,
  StorageAdapter,
  StorageResult,
  StorageMetadata,
  StorageOptions,
  StorageFilter,
  PersistenceEventType,
  PersistenceEventData
} from '../types/persistence-interfaces';
import { SerializedElement } from '../types/interfaces';

/**
 * Реализация менеджера персистентности
 */
export class PersistenceManagerImpl extends EventEmitter implements PersistenceManager {
  private adapters: Map<string, StorageAdapter> = new Map();
  private defaultAdapterName: string | null = null;

  constructor() {
    super();
  }

  /**
   * Регистрация адаптера хранилища
   */
  registerAdapter(adapter: StorageAdapter): void {
    if (this.adapters.has(adapter.name)) {
      throw new Error(`Адаптер с именем "${adapter.name}" уже зарегистрирован`);
    }

    this.adapters.set(adapter.name, adapter);

    // Подписываемся на события адаптера
    this.subscribeToAdapterEvents(adapter);

    // Если это первый адаптер, делаем его адаптером по умолчанию
    if (!this.defaultAdapterName) {
      this.defaultAdapterName = adapter.name;
    }

    this.emitEvent({
      type: PersistenceEventType.ADAPTER_REGISTERED,
      timestamp: Date.now(),
      adapterName: adapter.name
    });
  }

  /**
   * Получение адаптера по имени
   */
  getAdapter(name: string): StorageAdapter | null {
    return this.adapters.get(name) || null;
  }

  /**
   * Получение всех адаптеров
   */
  getAllAdapters(): StorageAdapter[] {
    return Array.from(this.adapters.values());
  }

  /**
   * Удаление адаптера
   */
  removeAdapter(name: string): boolean {
    const adapter = this.adapters.get(name);
    if (!adapter) {
      return false;
    }

    // Отписываемся от событий адаптера
    this.unsubscribeFromAdapterEvents(adapter);

    this.adapters.delete(name);

    // Если удаляем адаптер по умолчанию, выбираем новый
    if (this.defaultAdapterName === name) {
      const remainingAdapters = Array.from(this.adapters.keys());
      this.defaultAdapterName = remainingAdapters.length > 0 ? remainingAdapters[0] : null;
    }

    this.emitEvent({
      type: PersistenceEventType.ADAPTER_REMOVED,
      timestamp: Date.now(),
      adapterName: name
    });

    return true;
  }

  /**
   * Установка адаптера по умолчанию
   */
  setDefaultAdapter(name: string): void {
    if (!this.adapters.has(name)) {
      throw new Error(`Адаптер "${name}" не найден`);
    }

    this.defaultAdapterName = name;
  }

  /**
   * Получение адаптера по умолчанию
   */
  getDefaultAdapter(): StorageAdapter | null {
    return this.defaultAdapterName ? this.adapters.get(this.defaultAdapterName) || null : null;
  }

  /**
   * Сохранение элемента в указанное хранилище
   */
  async save(elementId: string, adapterName?: string, options?: StorageOptions): Promise<StorageResult<StorageMetadata>> {
    const adapter = this.getAdapterForOperation(adapterName);
    if (!adapter) {
      return {
        success: false,
        error: 'Адаптер хранилища не найден'
      };
    }

    // Получаем элемент из глобального реестра
    const element = CSElement.getElementById(elementId);
    if (!element) {
      return {
        success: false,
        error: `Элемент с ID "${elementId}" не найден`
      };
    }

    this.emitEvent({
      type: PersistenceEventType.SAVE_STARTED,
      timestamp: Date.now(),
      elementId,
      adapterName: adapter.name
    });

    try {
      // Сериализуем элемент
      const serializedData = element.serialize({
        includeChildren: true,
        includeData: true,
        includeMetadata: true
      });

      // Сохраняем в адаптере
      const result = await adapter.save(elementId, serializedData, options);

      if (result.success) {
        this.emitEvent({
          type: PersistenceEventType.SAVE_COMPLETED,
          timestamp: Date.now(),
          elementId,
          adapterName: adapter.name,
          metadata: result.metadata
        });
      } else {
        this.emitEvent({
          type: PersistenceEventType.SAVE_FAILED,
          timestamp: Date.now(),
          elementId,
          adapterName: adapter.name,
          error: result.error
        });
      }

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';

      this.emitEvent({
        type: PersistenceEventType.SAVE_FAILED,
        timestamp: Date.now(),
        elementId,
        adapterName: adapter.name,
        error: errorMessage
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Загрузка элемента из хранилища
   */
  async load(elementId: string, adapterName?: string, options?: StorageOptions): Promise<StorageResult<SerializedElement>> {
    const adapter = this.getAdapterForOperation(adapterName);
    if (!adapter) {
      return {
        success: false,
        error: 'Адаптер хранилища не найден'
      };
    }

    this.emitEvent({
      type: PersistenceEventType.LOAD_STARTED,
      timestamp: Date.now(),
      elementId,
      adapterName: adapter.name
    });

    try {
      const result = await adapter.load(elementId, options);

      if (result.success) {
        this.emitEvent({
          type: PersistenceEventType.LOAD_COMPLETED,
          timestamp: Date.now(),
          elementId,
          adapterName: adapter.name,
          metadata: result.metadata
        });
      } else {
        this.emitEvent({
          type: PersistenceEventType.LOAD_FAILED,
          timestamp: Date.now(),
          elementId,
          adapterName: adapter.name,
          error: result.error
        });
      }

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';

      this.emitEvent({
        type: PersistenceEventType.LOAD_FAILED,
        timestamp: Date.now(),
        elementId,
        adapterName: adapter.name,
        error: errorMessage
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Удаление элемента из хранилища
   */
  async delete(elementId: string, adapterName?: string, options?: StorageOptions): Promise<StorageResult<boolean>> {
    const adapter = this.getAdapterForOperation(adapterName);
    if (!adapter) {
      return {
        success: false,
        error: 'Адаптер хранилища не найден'
      };
    }

    this.emitEvent({
      type: PersistenceEventType.DELETE_STARTED,
      timestamp: Date.now(),
      elementId,
      adapterName: adapter.name
    });

    try {
      const result = await adapter.delete(elementId, options);

      if (result.success) {
        this.emitEvent({
          type: PersistenceEventType.DELETE_COMPLETED,
          timestamp: Date.now(),
          elementId,
          adapterName: adapter.name
        });
      } else {
        this.emitEvent({
          type: PersistenceEventType.DELETE_FAILED,
          timestamp: Date.now(),
          elementId,
          adapterName: adapter.name,
          error: result.error
        });
      }

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';

      this.emitEvent({
        type: PersistenceEventType.DELETE_FAILED,
        timestamp: Date.now(),
        elementId,
        adapterName: adapter.name,
        error: errorMessage
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Синхронизация между хранилищами
   */
  async sync(sourceAdapter: string, targetAdapter: string, options: {
    bidirectional?: boolean;
    conflictResolution?: 'source' | 'target' | 'latest' | 'merge';
    filter?: StorageFilter;
  } = {}): Promise<{
    synced: number;
    conflicts: number;
    errors: string[];
  }> {
    const source = this.adapters.get(sourceAdapter);
    const target = this.adapters.get(targetAdapter);

    if (!source || !target) {
      throw new Error('Один из адаптеров не найден');
    }

    this.emitEvent({
      type: PersistenceEventType.SYNC_STARTED,
      timestamp: Date.now(),
      metadata: { sourceAdapter, targetAdapter, options }
    });

    try {
      let synced = 0;
      let conflicts = 0;
      const errors: string[] = [];

      // Получаем все записи из источника
      const sourceRecords = await source.search(options.filter || {}, { includeData: true });

      for (const record of sourceRecords.records) {
        try {
          // Проверяем, существует ли запись в целевом хранилище
          const targetExists = await target.exists(record.id);

          if (targetExists) {
            // Есть конфликт - применяем стратегию разрешения
            const targetRecord = await target.load(record.id);
            
            if (targetRecord.success && targetRecord.metadata) {
              const shouldUpdate = this.resolveConflict(
                record.metadata,
                targetRecord.metadata,
                options.conflictResolution || 'latest'
              );

              if (shouldUpdate) {
                await target.save(record.id, record.data);
                synced++;
              } else {
                conflicts++;
              }
            }
          } else {
            // Записи нет в целевом хранилище - просто копируем
            await target.save(record.id, record.data);
            synced++;
          }

        } catch (error) {
          errors.push(`Ошибка синхронизации записи ${record.id}: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
        }
      }

      // Если синхронизация двунаправленная, повторяем в обратную сторону
      if (options.bidirectional) {
        const targetRecords = await target.search(options.filter || {}, { includeData: true });

        for (const record of targetRecords.records) {
          try {
            const sourceExists = await source.exists(record.id);

            if (!sourceExists) {
              await source.save(record.id, record.data);
              synced++;
            }

          } catch (error) {
            errors.push(`Ошибка обратной синхронизации записи ${record.id}: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
          }
        }
      }

      const result = { synced, conflicts, errors };

      this.emitEvent({
        type: PersistenceEventType.SYNC_COMPLETED,
        timestamp: Date.now(),
        metadata: { sourceAdapter, targetAdapter, result }
      });

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';

      this.emitEvent({
        type: PersistenceEventType.SYNC_FAILED,
        timestamp: Date.now(),
        error: errorMessage,
        metadata: { sourceAdapter, targetAdapter }
      });

      throw error;
    }
  }

  /**
   * Резервное копирование
   */
  async backup(adapterName: string, options: {
    includeMetadata?: boolean;
    compression?: boolean;
    encryption?: boolean;
  } = {}): Promise<{
    data: any;
    metadata: {
      timestamp: number;
      recordCount: number;
      size: number;
    };
  }> {
    const adapter = this.adapters.get(adapterName);
    if (!adapter) {
      throw new Error(`Адаптер "${adapterName}" не найден`);
    }

    // Получаем все записи
    const records = await adapter.search({}, { includeData: true });
    
    const backupData = {
      version: '1.0',
      timestamp: Date.now(),
      adapter: {
        name: adapter.name,
        type: adapter.type,
        config: adapter.config
      },
      records: records.records.map(record => ({
        id: record.id,
        data: record.data,
        metadata: options.includeMetadata ? record.metadata : undefined
      }))
    };

    // TODO: Добавить сжатие и шифрование если требуется
    
    const serializedData = JSON.stringify(backupData);
    const size = new TextEncoder().encode(serializedData).length;

    return {
      data: backupData,
      metadata: {
        timestamp: Date.now(),
        recordCount: records.records.length,
        size
      }
    };
  }

  /**
   * Восстановление из резервной копии
   */
  async restore(backupData: any, adapterName: string, options: {
    overwrite?: boolean;
    validateData?: boolean;
  } = {}): Promise<{
    restored: number;
    skipped: number;
    errors: string[];
  }> {
    const adapter = this.adapters.get(adapterName);
    if (!adapter) {
      throw new Error(`Адаптер "${adapterName}" не найден`);
    }

    let restored = 0;
    let skipped = 0;
    const errors: string[] = [];

    if (!backupData.records || !Array.isArray(backupData.records)) {
      throw new Error('Неверный формат резервной копии');
    }

    for (const record of backupData.records) {
      try {
        const exists = await adapter.exists(record.id);

        if (exists && !options.overwrite) {
          skipped++;
          continue;
        }

        // TODO: Добавить валидацию данных если требуется

        await adapter.save(record.id, record.data);
        restored++;

      } catch (error) {
        errors.push(`Ошибка восстановления записи ${record.id}: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
      }
    }

    return { restored, skipped, errors };
  }

  /**
   * Получение адаптера для операции
   */
  private getAdapterForOperation(adapterName?: string): StorageAdapter | null {
    if (adapterName) {
      return this.adapters.get(adapterName) || null;
    }

    return this.getDefaultAdapter();
  }

  /**
   * Разрешение конфликтов при синхронизации
   */
  private resolveConflict(
    sourceMetadata: StorageMetadata,
    targetMetadata: StorageMetadata,
    strategy: 'source' | 'target' | 'latest' | 'merge'
  ): boolean {
    switch (strategy) {
      case 'source':
        return true; // Всегда используем источник

      case 'target':
        return false; // Всегда используем цель

      case 'latest':
        return sourceMetadata.updatedAt > targetMetadata.updatedAt;

      case 'merge':
        // Простая стратегия слияния - используем более новую версию
        return sourceMetadata.version > targetMetadata.version;

      default:
        return false;
    }
  }

  /**
   * Подписка на события адаптера
   */
  private subscribeToAdapterEvents(adapter: StorageAdapter): void {
    // Проверяем, является ли адаптер EventEmitter
    if ('on' in adapter && typeof adapter.on === 'function') {
      (adapter as any).on('error', (error: any) => {
        this.emitEvent({
          type: PersistenceEventType.ADAPTER_ERROR,
          timestamp: Date.now(),
          adapterName: adapter.name,
          error: error.message || 'Неизвестная ошибка адаптера'
        });
      });
    }
  }

  /**
   * Отписка от событий адаптера
   */
  private unsubscribeFromAdapterEvents(adapter: StorageAdapter): void {
    // Проверяем, является ли адаптер EventEmitter
    if ('removeAllListeners' in adapter && typeof adapter.removeAllListeners === 'function') {
      (adapter as any).removeAllListeners();
    }
  }

  /**
   * Генерация события персистентности
   */
  private emitEvent(eventData: PersistenceEventData): void {
    this.emit('persistence:event', eventData);
    this.emit(eventData.type, eventData);
  }
} 